import ClearIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { SxProps, Theme } from "@mui/material/styles";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { type Dayjs } from "dayjs";
import { isEmpty, isNil } from "lodash";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import ReactJson from "react-json-view";
import Select, { type SingleValue } from "react-select";
import {
  definitionFor,
  multiSelectOptionsFor,
} from "shared/company-settings/helpers";
import type { SettingControl } from "shared/company-settings/registry.types";
import {
  CLEARABLE_SETTING_KEYS,
  COMPANY_SETTING_KEYS,
  type CompanySettingKey,
} from "shared/company-settings/schema";
import { parseNumberEntry } from "shared/company-settings/validate-setting-value";
import {
  getObjectUrlFromPresignedUrl,
  isNilOrEmptyString,
} from "shared/string";
import {
  type MeCompanyConfigurationFragment,
  type UpdateCompanyConfigurationInput,
  useCompaniesQuery,
  useCompanyConfigurationByCompanyUuidLazyQuery,
  useMeQuery,
  useSessionCompanyConfigurationLazyQuery,
  useUpdateCompanyConfigurationMutation,
} from "../../generated/graphql";
import { DragAndDropUpload } from "../common/drag-and-drop-upload";
import {
  asStringArray,
  asText,
  buildConfigurationUpdate,
  changedConfigurationKeys,
  companyOptionsWithSessionCompany,
  configurationReadSource,
  findConfigurationErrors,
  gateValueFor,
  isSelectableSettingKey,
  toCommittedTimeValue,
  toUpdatedUrlValue,
  type NumberEntries,
  type SelectableSettingKey,
} from "./company-configurations-utils";

/**
 * The one setting whose value is a file the operator uploads rather than a
 * link they paste. The registry describes it as a `url` because that is what
 * is stored, and this editor is the only place the file itself can be put in
 * S3, so the uploader lives beside the validated text box rather than instead
 * of it.
 */
const UPLOADABLE_SETTING_KEY = "invoicePdfLogoUrl";

const CHANGED_ROW_BACKGROUND = "#FDFD96";

/**
 * The value of the "Not set" option. MUI needs a string for every option, and
 * this one maps back to the `null` the server reads as "clear it".
 */
const NO_SELECTION = "";

const styles = {
  page: { padding: 4 } satisfies SxProps<Theme>,
  headerCell: { fontWeight: "bold" } satisfies SxProps<Theme>,
  valueHeaderCell: { fontWeight: "bold", width: 500 } satisfies SxProps<Theme>,
  changedRow: {
    backgroundColor: CHANGED_ROW_BACKGROUND,
  } satisfies SxProps<Theme>,
  selectField: { minWidth: 200 } satisfies SxProps<Theme>,
  uploadRow: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
  } satisfies SxProps<Theme>,
  hiddenClearButton: { visibility: "hidden" } satisfies SxProps<Theme>,
  visibleClearButton: { visibility: "visible" } satisfies SxProps<Theme>,
  submitRow: {
    alignItems: "center",
    display: "flex",
  } satisfies SxProps<Theme>,
  settingKey: { color: "text.secondary" } satisfies SxProps<Theme>,
};

const logoPreviewStyle = { maxWidth: "100px" };

type ConfigurationValueFieldProps = {
  readonly settingKey: SelectableSettingKey;
  readonly label: string;
  readonly control: SettingControl;
  readonly value: unknown;
  readonly gateValue: unknown;
  readonly numberEntry: string | undefined;
  readonly error: string | undefined;
  readonly onChange: (key: SelectableSettingKey, value: unknown) => void;
  readonly onNumberEntry: (key: SelectableSettingKey, text: string) => void;
};

/** The registry's `SettingControl` rendered as the matching MUI control. */
const ConfigurationValueField = ({
  settingKey,
  label,
  control,
  value,
  gateValue,
  numberEntry,
  error,
  onChange,
  onNumberEntry,
}: ConfigurationValueFieldProps) => {
  const handleSwitch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(settingKey, event.target.checked);
    },
    [onChange, settingKey],
  );

  const handleNumber = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onNumberEntry(settingKey, event.target.value);
    },
    [onNumberEntry, settingKey],
  );

  const handleText = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(settingKey, event.target.value);
    },
    [onChange, settingKey],
  );

  const handleUrl = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(settingKey, toUpdatedUrlValue(event.target.value));
    },
    [onChange, settingKey],
  );

  const handleSelect = useCallback(
    (event: SelectChangeEvent) => {
      onChange(
        settingKey,
        event.target.value === NO_SELECTION ? null : event.target.value,
      );
    },
    [onChange, settingKey],
  );

  const handleMultiSelect = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onChange(settingKey, event.target.value);
    },
    [onChange, settingKey],
  );

  const handleTime = useCallback(
    (next: Dayjs | null) => {
      onChange(settingKey, toCommittedTimeValue(next));
    },
    [onChange, settingKey],
  );

  const handleUpload = useCallback(
    (presignedUrl: string) => {
      const objectUrl = getObjectUrlFromPresignedUrl(presignedUrl);
      if (!isNil(objectUrl)) {
        onChange(settingKey, objectUrl);
      }
    },
    [onChange, settingKey],
  );

  const handleClearUpload = useCallback(() => {
    onChange(settingKey, null);
  }, [onChange, settingKey]);

  const labelProps = useMemo(() => ({ "aria-label": label }), [label]);
  const timeSlotProps = useMemo(
    () => ({ textField: { inputProps: labelProps } }),
    [labelProps],
  );
  const multiSelectOptions = useMemo(
    () =>
      control.type === "multi-select"
        ? multiSelectOptionsFor(control, gateValue)
        : [],
    [control, gateValue],
  );

  switch (control.type) {
    case "switch": {
      return (
        <Switch
          checked={value === true}
          inputProps={labelProps}
          onChange={handleSwitch}
        />
      );
    }
    case "number": {
      return (
        <TextField
          error={error !== undefined}
          helperText={error}
          inputProps={labelProps}
          size="small"
          type="number"
          value={numberEntry ?? asText(value)}
          onChange={handleNumber}
        />
      );
    }
    case "text": {
      return (
        <TextField
          fullWidth
          inputProps={labelProps}
          maxRows={control.multiline === true ? 4 : undefined}
          multiline={control.multiline === true}
          size="small"
          value={asText(value)}
          onChange={handleText}
        />
      );
    }
    case "url": {
      const urlField = (
        <TextField
          fullWidth
          error={error !== undefined}
          helperText={error ?? "Empty the field to clear it"}
          inputProps={labelProps}
          size="small"
          value={asText(value)}
          onChange={handleUrl}
        />
      );
      if (settingKey !== UPLOADABLE_SETTING_KEY) {
        return urlField;
      }
      const fileUrl = typeof value === "string" ? value : undefined;
      return (
        <>
          <Box sx={styles.uploadRow}>
            <DragAndDropUpload
              isSmall
              fileUrl={fileUrl}
              setPresignedUrl={handleUpload}
            />
            <IconButton
              aria-label={`Clear ${label}`}
              size="small"
              sx={
                isNilOrEmptyString(fileUrl)
                  ? styles.hiddenClearButton
                  : styles.visibleClearButton
              }
              onClick={handleClearUpload}
            >
              <ClearIcon />
            </IconButton>
            <img alt={fileUrl ?? ""} src={fileUrl} style={logoPreviewStyle} />
          </Box>
          {urlField}
        </>
      );
    }
    case "select": {
      const clearable = CLEARABLE_SETTING_KEYS.has(settingKey);
      return (
        <MuiSelect
          displayEmpty={clearable}
          inputProps={labelProps}
          size="small"
          sx={styles.selectField}
          value={asText(value)}
          onChange={handleSelect}
        >
          {clearable ? <MenuItem value={NO_SELECTION}>Not set</MenuItem> : null}
          {control.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
      );
    }
    case "multi-select": {
      return (
        <MuiSelect
          multiple
          inputProps={labelProps}
          size="small"
          sx={styles.selectField}
          value={asStringArray(value)}
          onChange={handleMultiSelect}
        >
          {multiSelectOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
      );
    }
    case "time": {
      const time = typeof value === "string" ? dayjs(value) : null;
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            slotProps={timeSlotProps}
            value={time}
            onChange={handleTime}
          />
        </LocalizationProvider>
      );
    }
  }
};
ConfigurationValueField.displayName = "ConfigurationValueField";

type SettingNameCellProps = {
  readonly settingKey: CompanySettingKey;
};

const SettingNameCell = ({ settingKey }: SettingNameCellProps) => (
  <TableCell>
    <Typography variant="body2">{definitionFor(settingKey).label}</Typography>
    <Typography sx={styles.settingKey} variant="caption">
      {settingKey}
    </Typography>
  </TableCell>
);
SettingNameCell.displayName = "SettingNameCell";

type SettingMetadataCellsProps = {
  readonly settingKey: CompanySettingKey;
};

const SettingMetadataCells = ({ settingKey }: SettingMetadataCellsProps) => {
  const definition = definitionFor(settingKey);
  return (
    <>
      <TableCell>{definition.control.type}</TableCell>
      <TableCell>{definition.description}</TableCell>
      <TableCell>{definition.page}</TableCell>
      <TableCell>{definition.surfaces.join(", ")}</TableCell>
      <TableCell>{definition.exposedToUser ? "Yes" : "No"}</TableCell>
    </>
  );
};
SettingMetadataCells.displayName = "SettingMetadataCells";

type ConfigurationRowProps = {
  readonly settingKey: SelectableSettingKey;
  readonly value: unknown;
  readonly gateValue: unknown;
  readonly numberEntry: string | undefined;
  readonly changed: boolean;
  readonly error: string | undefined;
  readonly onChange: (key: SelectableSettingKey, value: unknown) => void;
  readonly onNumberEntry: (key: SelectableSettingKey, text: string) => void;
};

const ConfigurationRow = memo(
  ({
    settingKey,
    value,
    gateValue,
    numberEntry,
    changed,
    error,
    onChange,
    onNumberEntry,
  }: ConfigurationRowProps) => (
    <TableRow sx={changed ? styles.changedRow : undefined}>
      <SettingNameCell settingKey={settingKey} />
      <TableCell>
        <ConfigurationValueField
          control={definitionFor(settingKey).control}
          error={error}
          gateValue={gateValue}
          label={definitionFor(settingKey).label}
          numberEntry={numberEntry}
          settingKey={settingKey}
          value={value}
          onChange={onChange}
          onNumberEntry={onNumberEntry}
        />
      </TableCell>
      <SettingMetadataCells settingKey={settingKey} />
    </TableRow>
  ),
);
ConfigurationRow.displayName = "ConfigurationRow";

type UnavailableConfigurationRowProps = {
  readonly settingKey: CompanySettingKey;
};

/**
 * A registry key the entity does not expose. It is listed so the table stays
 * the whole catalog, and it carries the reason rather than an empty control
 * that would write a value nobody can read back.
 */
const UnavailableConfigurationRow = memo(
  ({ settingKey }: UnavailableConfigurationRowProps) => (
    <TableRow>
      <SettingNameCell settingKey={settingKey} />
      <TableCell>Not exposed by the API -- not editable here</TableCell>
      <SettingMetadataCells settingKey={settingKey} />
    </TableRow>
  ),
);
UnavailableConfigurationRow.displayName = "UnavailableConfigurationRow";

const CompanyConfigurations = () => {
  const [updateCompanyConfiguration, { loading: updateLoading }] =
    useUpdateCompanyConfigurationMutation();
  const [getConfiguration, { loading: crossCompanyLoading }] =
    useCompanyConfigurationByCompanyUuidLazyQuery();
  // Network-only so a save is read back rather than served from cache.
  const [getSessionConfiguration, { loading: sessionLoading }] =
    useSessionCompanyConfigurationLazyQuery({ fetchPolicy: "network-only" });
  const loading = crossCompanyLoading || sessionLoading;
  const [configuration, setConfiguration] = useState<
    MeCompanyConfigurationFragment | undefined
  >();
  const [newConfiguration, setNewConfiguration] =
    useState<UpdateCompanyConfigurationInput>({ uuid: "" });
  const [numberEntries, setNumberEntries] = useState<NumberEntries>({});
  const { data: companiesData } = useCompaniesQuery();
  const { data: meData } = useMeQuery();
  const sessionCompany = meData?.me?.company;
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const companySelectOptions = useMemo(
    () =>
      companyOptionsWithSessionCompany(
        companiesData?.companies,
        sessionCompany,
      ),
    [companiesData, sessionCompany],
  );

  const fetchConfiguration = async () => {
    if (isNil(companyUuid)) {
      return;
    }
    if (
      configurationReadSource(companyUuid, sessionCompany?.uuid) === "session"
    ) {
      const res = await getSessionConfiguration();
      setConfiguration(res.data?.companyConfiguration ?? undefined);
      return;
    }
    const res = await getConfiguration({
      variables: {
        uuid: companyUuid,
      },
    });
    setConfiguration(res.data?.companyConfigurationByCompanyUuid ?? undefined);
  };

  useEffect(() => {
    fetchConfiguration();
  }, [companyUuid]);

  useEffect(() => {
    setNumberEntries({});
    if (isNil(configuration)) {
      setNewConfiguration({ uuid: "" });
    } else {
      const configCopy = { ...configuration };
      delete configCopy.__typename;
      setNewConfiguration(configCopy);
    }
  }, [configuration]);

  const [successVisible, setSuccessVisible] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);

  const DEFAULT_MESSAGE = "Error updating company configuration";
  const [errorMessage, setErrorMessage] = useState(DEFAULT_MESSAGE);
  const [results, setResults] = useState({});

  const configurationErrors = useMemo(
    () => findConfigurationErrors(newConfiguration, numberEntries),
    [newConfiguration, numberEntries],
  );

  const changedKeys = useMemo<ReadonlySet<string>>(
    () =>
      isNil(configuration)
        ? new Set()
        : new Set(changedConfigurationKeys(configuration, newConfiguration)),
    [configuration, newConfiguration],
  );

  const handleChange = useCallback(
    (key: SelectableSettingKey, value: unknown) => {
      setNewConfiguration((previous) => ({ ...previous, [key]: value }));
    },
    [],
  );

  const handleNumberEntry = useCallback(
    (key: SelectableSettingKey, text: string) => {
      setNumberEntries((previous) => ({ ...previous, [key]: text }));
      const { control } = definitionFor(key);
      if (control.type !== "number") {
        return;
      }
      const parsed = parseNumberEntry(
        text,
        control,
        CLEARABLE_SETTING_KEYS.has(key),
      );
      if (parsed.ok) {
        handleChange(key, parsed.value);
      }
    },
    [handleChange],
  );

  const onSubmit = async () => {
    try {
      if (isNil(configuration) || isNilOrEmptyString(newConfiguration.uuid)) {
        throw new Error("Company configuration not found");
      }
      const response = await updateCompanyConfiguration({
        variables: {
          updateCompanyConfigurationInput: buildConfigurationUpdate(
            configuration,
            newConfiguration,
          ),
        },
      });
      fetchConfiguration();
      const { errors } = response;
      if (isEmpty(errors)) {
        setResults(response.data?.updateCompanyConfiguration ?? {});
        setSuccessVisible(true);
      } else {
        setErrorMessage(
          errors?.map((err) => err.message).join(", ") ?? DEFAULT_MESSAGE,
        );
        setErrorVisible(true);
      }
    } catch (error) {
      setErrorVisible(true);
      setErrorMessage(`Error: ${error}`);

      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const hasConfiguration = newConfiguration.uuid.length > 0 && !loading;

  return (
    <Grid container spacing={1} sx={styles.page}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={successVisible}
      >
        <Alert
          severity="success"
          onClose={() => {
            setSuccessVisible(false);
          }}
        >
          Successfully updated company configuration
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={errorVisible}
      >
        <Alert
          severity="error"
          onClose={() => {
            setErrorVisible(false);
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Grid item xs={12}>
        <Typography variant="h4">Company Configuration Editor</Typography>
        <Select
          options={companySelectOptions}
          placeholder="Select company"
          onChange={(option: SingleValue<{ label: string; value: string }>) => {
            if (!isNil(option) && !isNil(option.value)) {
              setCompanyUuid(option.value);
            }
          }}
        />
        <Typography variant="subtitle1">
          {`Note: For configs that are already exposed in Pallet's UI,
                    please edit from the UI directly if possible.`}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={styles.headerCell}>Name</TableCell>
                <TableCell sx={styles.valueHeaderCell}>Value</TableCell>
                <TableCell sx={styles.headerCell}>Control</TableCell>
                <TableCell sx={styles.headerCell}>Description</TableCell>
                <TableCell sx={styles.headerCell}>Page</TableCell>
                <TableCell sx={styles.headerCell}>Surfaces</TableCell>
                <TableCell sx={styles.headerCell}>Exposed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasConfiguration ? (
                COMPANY_SETTING_KEYS.map((settingKey) =>
                  isSelectableSettingKey(settingKey) ? (
                    <ConfigurationRow
                      key={settingKey}
                      changed={changedKeys.has(settingKey)}
                      error={configurationErrors[settingKey]}
                      gateValue={gateValueFor(settingKey, newConfiguration)}
                      numberEntry={numberEntries[settingKey]}
                      settingKey={settingKey}
                      value={newConfiguration[settingKey]}
                      onChange={handleChange}
                      onNumberEntry={handleNumberEntry}
                    />
                  ) : (
                    <UnavailableConfigurationRow
                      key={settingKey}
                      settingKey={settingKey}
                    />
                  ),
                )
              ) : (
                <TableRow>
                  <TableCell>No configuration found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} sx={styles.submitRow}>
        <Button
          disabled={updateLoading || !isEmpty(configurationErrors)}
          variant="contained"
          onClick={onSubmit}
        >
          Update Company Configuration
        </Button>
      </Grid>
      <Grid item xs={12}>
        <ReactJson src={results} />
      </Grid>
    </Grid>
  );
};

export default CompanyConfigurations;
